import { useQueries } from "react-query";

import { getV3ClientAssessments, V3_ASSESSMENT_SEGMENTS } from "api/assessments";
import { GET_V3_CLIENT_ASSESSMENTS, STALE_TIME_5_MINUTES } from "@constants/reactQueryKeys";
import type { AssessmentType } from "components/ClientDetails/Sections/Assessments/types";

interface V3QueryEntry {
  assessmentType: AssessmentType;
  segment: string;
}

export interface AssessmentQueryStatus {
  isLoading: boolean;
  isError: boolean;
  isNotFound: boolean;
  refetch: () => Promise<unknown>;
}

const V3_QUERIES: V3QueryEntry[] = Object.entries(V3_ASSESSMENT_SEGMENTS).map(
  ([type, segment]) => ({ assessmentType: type as AssessmentType, segment: segment! })
);

/**
 * Fetches all v3 assessment types for a client in parallel.
 * Each assessment type is fetched independently; individual failures are handled quietly.
 *
 * @param clientId - The client's unique identifier
 * @returns Combined raw data keyed by assessment type, plus aggregate loading/error states
 */
export function useGetV3ClientAssessments(clientId?: string | number) {
  // Normalize clientId to a string so query keys are consistent whether callers
  // pass a number (e.g., from selectors) or a string.
  const clientIdStr = clientId !== undefined && clientId !== null ? String(clientId) : undefined;

  const results = useQueries(
    V3_QUERIES.map(({ assessmentType, segment }) => ({
      queryKey: [GET_V3_CLIENT_ASSESSMENTS, clientIdStr, assessmentType],
      queryFn: () => {
        if (!clientIdStr) {
          return Promise.resolve({ data: [] });
        }

        return getV3ClientAssessments(clientIdStr, segment);
      },
      enabled: !!clientIdStr,
      staleTime: STALE_TIME_5_MINUTES,
      refetchOnWindowFocus: false,
      retry: false,
    }))
  );

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.length > 0 && results.every((r) => r.isError);

  const rawDataByType: Partial<Record<AssessmentType, unknown[]>> = {};
  const queryStatusByType: Partial<Record<AssessmentType, AssessmentQueryStatus>> = {};

  results.forEach((result, index) => {
    const { assessmentType } = V3_QUERIES[index];
    rawDataByType[assessmentType] = result.data?.data ?? [];

    const statusCode = (result.error as { response?: { status?: number } } | undefined)?.response
      ?.status;

    queryStatusByType[assessmentType] = {
      isLoading: result.isLoading,
      isError: result.isError,
      isNotFound: result.isError && statusCode === 404,
      refetch: result.refetch,
    };
  });

  return { rawDataByType, queryStatusByType, isLoading, isError };
}
